import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  Plus,
  Search,
  Filter,
  Mail,
  Phone,
  User,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Edit,
  Trash2,
  Download,
  Upload,
  Tag,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  subscribed: 'badge-success',
  unsubscribed: 'badge-gray',
  bounced: 'badge-danger',
  pending: 'badge-warning'
};

const tenantStatusColors = {
  trial: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [stats, setStats] = useState({ total: 0, subscribed: 0, unsubscribed: 0, bounced: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    tags: []
  });

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    tag: searchParams.get('tag') || '',
    tenantId: searchParams.get('tenantId') || ''
  });
  const [tenantName, setTenantName] = useState('');

  useEffect(() => {
    fetchContacts();
  }, [searchParams]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const tenantId = searchParams.get('tenantId') || '';
      const params = {
        page: searchParams.get('page') || 1,
        search: searchParams.get('search') || '',
        status: searchParams.get('status') || '',
        lifecycleStage: searchParams.get('lifecycleStage') || '',
        tenantId: tenantId,
        limit: 20
      };

      const response = await adminApi.getMarketingContacts(params);
      setContacts(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
      setStats(response.data.stats || { total: 0, subscribed: 0, unsubscribed: 0, bounced: 0 });

      // Set tenant name if filtering by tenantId
      if (tenantId && response.data.data?.length > 0) {
        const contact = response.data.data[0];
        if (contact.tenantDomain) {
          setTenantName(contact.companyName || contact.tenantDomain);
        }
      } else if (!tenantId) {
        setTenantName('');
      }
    } catch (err) {
      toast.error('Failed to load contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (filters.search) {
      newParams.set('search', filters.search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createMarketingContact(formData);
      toast.success('Contact created successfully');
      setShowCreateModal(false);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        phone: '',
        tags: []
      });
      fetchContacts();
    } catch (err) {
      toast.error('Failed to create contact');
      console.error(err);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!confirm(`Are you sure you want to delete ${contact.email}?`)) return;

    try {
      await adminApi.deleteMarketingContact(contact.id);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (err) {
      toast.error('Failed to delete contact');
      console.error(err);
    }
  };

  const handleExportContacts = async () => {
    try {
      toast.success('Export started. You will receive an email when ready.');
      await adminApi.exportMarketingContacts(filters);
    } catch (err) {
      toast.error('Failed to export contacts');
      console.error(err);
    }
  };

  const handleSyncTenantUsers = async () => {
    setSyncing(true);
    try {
      const response = await adminApi.syncTenantUsers();
      const { totalSynced, totalTenants, totalErrors } = response.data;
      toast.success(`Synced ${totalSynced} users from ${totalTenants} tenants${totalErrors > 0 ? ` (${totalErrors} errors)` : ''}`);
      fetchContacts();
    } catch (err) {
      toast.error('Failed to sync tenant users');
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedContacts(contacts.map((c) => c.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {searchParams.get('tenantId') ? 'Tenant Users' : 'Marketing Contacts'}
          </h1>
          <p className="text-gray-500 mt-1">
            {searchParams.get('tenantId')
              ? `Showing users for ${tenantName || 'selected tenant'}`
              : 'Manage your email marketing contacts'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSyncTenantUsers}
            disabled={syncing}
            className="btn-secondary"
            title="Sync all users from tenant databases"
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Users'}
          </button>
          <button onClick={handleExportContacts} className="btn-secondary">
            <Download className="w-5 h-5 mr-2" />
            Export
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Tenant Filter Banner */}
      {searchParams.get('tenantId') && (
        <div className="card p-4 bg-blue-50 border-blue-200 flex items-center justify-between">
          <div className="flex items-center">
            <User className="w-5 h-5 text-blue-600 mr-2" />
            <span className="text-blue-800">
              Showing users from: <strong>{tenantName || 'Selected Tenant'}</strong>
            </span>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/tenants/${searchParams.get('tenantId')}`}
              className="btn-secondary text-sm"
            >
              View Tenant
            </Link>
            <button
              onClick={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('tenantId');
                setSearchParams(newParams);
              }}
              className="btn-secondary text-sm"
            >
              Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
          <p className="text-sm text-gray-500">Total Contacts</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-green-600">{stats.subscribed || 0}</p>
          <p className="text-sm text-gray-500">Subscribed</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-gray-500">{stats.unsubscribed || 0}</p>
          <p className="text-sm text-gray-500">Unsubscribed</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-red-600">{stats.bounced || 0}</p>
          <p className="text-sm text-gray-500">Bounced</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="input pl-10"
              />
            </div>
          </form>

          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">All Status</option>
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="bounced">Bounced</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedContacts.length > 0 && (
        <div className="card p-4 bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-primary-700">
              {selectedContacts.length} contact(s) selected
            </p>
            <div className="flex gap-2">
              <button className="btn-secondary text-sm">Add to List</button>
              <button className="btn-secondary text-sm text-red-600 hover:text-red-700">
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header w-10">
                  <input
                    type="checkbox"
                    checked={selectedContacts.length === contacts.length && contacts.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300"
                  />
                </th>
                <th className="table-header">Contact</th>
                <th className="table-header">Company / Tenant</th>
                <th className="table-header">Tenant Status</th>
                <th className="table-header">Status</th>
                <th className="table-header">Tags</th>
                <th className="table-header">Created</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    No contacts found
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.location.href = `/marketing/contacts/${contact.id}`}
                  >
                    <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300"
                      />
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        {contact.avatarUrl ? (
                          <img
                            src={contact.avatarUrl}
                            alt=""
                            className="w-8 h-8 rounded-full mr-3 object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-primary-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {contact.fullName || contact.email}
                          </p>
                          <p className="text-xs text-gray-500">{contact.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div>
                        <p className="text-gray-900">{contact.companyName || '-'}</p>
                        {contact.tenantDomain && (
                          <p className="text-xs text-primary-600">practicemanagerpro.com.au/t/{contact.tenantDomain}</p>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      {contact.tenantStatus ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${tenantStatusColors[contact.tenantStatus] || 'bg-gray-100 text-gray-800'}`}>
                          {contact.tenantStatus.charAt(0).toUpperCase() + contact.tenantStatus.slice(1)}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">No tenant</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={statusColors[contact.status] || 'badge-gray'}>
                        {contact.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags?.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                        {contact.tags?.length > 2 && (
                          <span className="text-xs text-gray-400">
                            +{contact.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-gray-500">
                      {new Date(contact.createdAt).toLocaleDateString()}
                    </td>
                    <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/marketing/contacts/${contact.id}`}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="View"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteContact(contact)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to{' '}
              {Math.min(pagination.page * 20, pagination.total)} of {pagination.total} contacts
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add New Contact</h3>
            </div>

            <form onSubmit={handleCreateContact} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
